/**
 * `/portal/operator/payments` — the operator's statement for a calendar
 * month (the same period the monthly settlement email uses): its fees, the
 * cash its drivers collected, what Pickup Hungary has already paid, what is
 * still owed, and the per-trip lines behind those totals.
 *
 * Everything comes from `/portal/operator/statement`, already reduced to
 * the operator's own figures in its settlement currency.
 *
 * A company that settles in two currencies holds two operator records — one
 * per currency, because a record carries exactly one `settlement_currency`
 * and that is what keeps every figure in an unambiguous bucket. When the
 * login answers for more than one, the API sends each record's own statement
 * under `by_operator` and this screen shows them one after the other, each
 * labelled with its currency. There is no combined total, and there must
 * never be one: nothing converts between EUR and HUF here, and a sum of the
 * two would be a number with no meaning under it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { fetchOperatorStatement } from '../../../Services/OperatorPortalApi';
import { ChevronLeftIcon, ChevronRightIcon } from '../Components/Icons';
import StatementBucket from './StatementBucket';
import {
  monthRange, operatorLoadErrorMessage, statementBuckets,
} from './operatorFormat';

export default function OperatorPayments() {
  // 0 = this month, -1 = last month, …; never into the future.
  const [offset, setOffset] = useState(0);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const period = useMemo(() => monthRange(offset), [offset]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchOperatorStatement({ from: period.from, to: period.to })
      .then((result) => { if (!cancelled) setStatement(result); })
      .catch((failure) => {
        if (cancelled) return;
        setStatement(null);
        setError(operatorLoadErrorMessage(failure));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const buckets = useMemo(() => statementBuckets(statement), [statement]);
  const split = buckets.length > 1;

  return (
    <div className="pp-op-page">
      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">Payments</h1>
          <div className="pp-op-subtitle">What your trips earn, and what is still owed to you</div>
        </div>
        <div className="pp-op-month" role="group" aria-label="Month">
          <button
            type="button"
            className="pp-btn pp-op-month__step"
            onClick={() => setOffset((value) => value - 1)}
            aria-label="Previous month"
          >
            <ChevronLeftIcon size={20} />
          </button>
          <span className="pp-op-month__label" aria-live="polite">{period.label}</span>
          <button
            type="button"
            className="pp-btn pp-op-month__step"
            onClick={() => setOffset((value) => Math.min(0, value + 1))}
            disabled={offset >= 0}
            aria-label="Next month"
          >
            <ChevronRightIcon size={20} />
          </button>
        </div>
      </div>

      {error ? <div className="pp-op-error" role="alert">{error}</div> : null}
      {loading ? <div className="pp-state" role="status">Loading your statement…</div> : null}

      {!loading && statement ? (
        <>
          {split ? (
            <p className="pp-op-footnote" data-testid="statement-split-note">
              {`You are settled in ${buckets.map((bucket) => bucket.currency).join(' and ')}. `
                + 'Each is paid on its own and shown on its own below — they are not added together.'}
            </p>
          ) : null}

          {buckets.map((bucket, index) => (
            <StatementBucket
              key={bucket.operator_id || `bucket-${index}`}
              bucket={bucket}
              periodLabel={period.label}
              title={split ? `${period.label} · ${bucket.currency}` : period.label}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}
